// Sugalarm — canonical resolver for live glucose thresholds (cycle 35).
//
// Every UI surface that maps a glucose value to a zone (hero color,
// pill tone, sparkline / history-chart dot color, household member
// cards, MobileFrame collapsed-header readout) reads thresholds from
// here. The same `useSettings()` source that drives `useAlarmEpisode`
// also drives this hook — one source of truth, no more cases where
// the banner says LOW while the hero says In range.
//
// `DEFAULT_THRESHOLDS` is the loading-state fallback only — once
// `/api/settings` has resolved, the user's live thresholds replace it.
// Memoized on the four threshold fields so consumers don't re-render
// on unrelated settings churn (units, voice, …).

import { useMemo } from "react";

import { useSettings } from "./useSettings";
import { DEFAULT_THRESHOLDS, type Thresholds } from "../lib/types";

export type { Thresholds } from "../lib/types";

export function useThresholds(): Thresholds {
  const { settings } = useSettings();
  const urgent_low = settings?.urgent_low;
  const target_low = settings?.target_low;
  const target_high = settings?.target_high;
  const urgent_high = settings?.urgent_high;
  return useMemo<Thresholds>(() => {
    if (
      urgent_low == null ||
      target_low == null ||
      target_high == null ||
      urgent_high == null
    ) {
      return DEFAULT_THRESHOLDS;
    }
    return { urgent_low, target_low, target_high, urgent_high };
  }, [urgent_low, target_low, target_high, urgent_high]);
}
